import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Header } from '../components/Header';
import { BottomNavigation } from '../components/BottomNavigation';
import { QuantityControl } from '../components/QuantityControl';
import { useCartStore } from '../stores/useCartStore';
import { useAuthStore } from '../stores/useAuthStore';
import { useCurrencySymbol } from '../lib/currency';
import { getFullImageUrl } from '../lib/api';
import { Trash2, ShoppingBag, MessageSquare, Receipt, Calendar, ChevronDown, Sparkles } from 'lucide-react';

export function CartPage() {
  const { tableId } = useParams<{ tableId: string }>();
  const navigate = useNavigate();
  const { orderMode } = useAuthStore();
  const currencySymbol = useCurrencySymbol();
  const {
    items,
    offers,
    notes,
    setNotes,
    updateQuantity,
    updateOfferQuantity,
    updateItemSubProducts,
    updateOfferObservation,
    removeItem,
    removeOffer,
    getTotal,
    scheduledOrders,
    updateScheduledQuantity,
    updateScheduledOfferQuantity,
    updateScheduledItemSubProducts,
    updateScheduledOfferObservation,
    removeScheduledItem,
    removeScheduledOffer,
    removeScheduledDate,
    getTotalByDate,
    hasScheduledOrders,
    hasImmediateOrders
  } = useCartStore();

  const [expandedDates, setExpandedDates] = useState<Set<string>>(new Set());


  const total = getTotal();

  const toggleDate = (date: string) => {
    const newExpanded = new Set(expandedDates);
    if (newExpanded.has(date)) {
      newExpanded.delete(date);
    } else {
      newExpanded.add(date);
    }
    setExpandedDates(newExpanded);
  };

  const formatDate = (dateString: string) => {
    const [year, month, day] = dateString.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };
    return date.toLocaleDateString('es-AR', options);
  };


  const handleProceedToOrder = () => {
    if (!hasImmediateOrders() && !hasScheduledOrders()) return;

    // Con el nuevo flujo de onboarding, ya tenemos orderMode en el store
    navigate(`/order-confirmation/${tableId}`, {
      state: { orderType: orderMode }
    });
  };

  if (!hasImmediateOrders() && !hasScheduledOrders()) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-50 to-sky-50 dark:from-gray-900 dark:to-gray-800 pb-24">
        <Header title="Carrito" showBack />
        <div className="flex flex-col items-center justify-center py-20 px-6">
          <div className="text-center">
            <div className="w-32 h-32 bg-gradient-to-br from-sky-100 to-sky-100 dark:from-sky-900/20 dark:to-sky-900/20 rounded-full flex items-center justify-center mx-auto mb-8">
              <ShoppingBag className="w-16 h-16 text-primary" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
              Tu carrito está vacío
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-sm">
              Agrega algunos productos del menú para continuar con tu pedido
            </p>
            <button
              onClick={() => navigate(`/menu/${tableId}`)}
              className="bg-gradient-to-r btn-primary text-white px-8 py-4 rounded-2xl font-bold transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105"
            >
              Explorar Menú
            </button>
          </div>
        </div>
        <BottomNavigation tableId={tableId || ''} />
      </div>
    );
  }

  // Calculate total for all orders (immediate + scheduled)
  const grandTotal = total + scheduledOrders.reduce((sum, order) => sum + getTotalByDate(order.scheduledDate), 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-sky-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 pb-40">
      <Header title="Carrito" showBack showServiceIndicator={true} />

      <div className="p-6 space-y-6">
        {/* Immediate Orders Section */}
        {hasImmediateOrders() && (
          <div className="space-y-4">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <ShoppingBag className="w-5 h-5 text-primary" />
              Pedido Inmediato
            </h2>

            {/* Cart Items */}
            {items.map((item) => (
              <div
                key={item.cartItemId}
                className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-2xl p-5 shadow-lg border border-gray-100 dark:border-gray-700 hover:shadow-xl transition-all duration-200"
              >
                <div className="flex items-center justify-between gap-4">
                  <img
                    src={getFullImageUrl(item.product.image)}
                    alt={item.product.name}
                    className="w-16 h-16 rounded-xl object-cover shadow-md flex-shrink-0"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = 'https://via.placeholder.com/150?text=No+Image';
                      console.error('Image failed to load in cart:', item.product.image);
                    }}
                  />
                  <div className="flex flex-col justify-center flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="font-bold text-base text-gray-900 dark:text-white truncate">
                        {item.product.name}
                      </h3>
                      <span className="font-bold text-base text-gray-900 dark:text-white shrink-0">
                        {currencySymbol}{((item.product.price + (item.sub_products?.reduce((acc, sub) => acc + sub.additional_price, 0) || 0)) * item.quantity).toFixed(2)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                      {currencySymbol}{item.product.price.toFixed(2)} c/u
                    </p>
                    <div className="mt-2 flex items-center justify-between flex-wrap gap-2">
                      <QuantityControl
                        quantity={item.quantity}
                        onIncrease={() => updateQuantity(item.cartItemId || item.product.uuid, item.quantity + 1)}
                        onDecrease={() => updateQuantity(item.cartItemId || item.product.uuid, item.quantity - 1)}
                      />
                      <button
                        onClick={() => removeItem(item.cartItemId || item.product.uuid)}
                        className="p-2 text-primary hover:text-sky-700 hover:bg-sky-50 dark:hover:bg-sky-900/20 rounded-full transition-all duration-200"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                    {/* Side Dish Selector (Dynamic) */}
                    {item.product.sub_products && item.product.sub_products.length > 0 && (
                      <div className="mt-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2 text-xs font-black text-gray-400 uppercase tracking-widest">
                            <MessageSquare className="w-3.5 h-3.5" />
                            <span>Guarniciones</span>
                          </div>
                          {item.sub_products && item.sub_products.length > 0 && (
                            <span className="text-[10px] font-black text-primary uppercase">
                              {item.sub_products.length} de {item.product.sub_products.length}
                            </span>
                          )}
                        </div>
                        <div className="flex gap-4 overflow-x-auto no-scrollbar -mx-5 px-5 pb-1">
                          {item.product.sub_products.map((sub) => {
                            const isSelected = item.sub_products?.some(s => s.sub_product_uuid === sub.sub_product_uuid);
                            return (
                                <button
                                  key={sub.sub_product_uuid}
                                  onClick={() => {
                                      if (isSelected) {
                                          updateItemSubProducts(item.cartItemId || item.product.uuid, []);
                                      } else {
                                          updateItemSubProducts(item.cartItemId || item.product.uuid, [sub]);
                                      }
                                  }}
                                  className={`relative flex-shrink-0 w-24 h-32 rounded-2xl overflow-hidden transition-all duration-300 border-2 ${isSelected
                                      ? 'border-primary shadow-lg shadow-primary/20 scale-105'
                                      : 'border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50'
                                    }`}
                                >
                                    <div className="h-16 w-full overflow-hidden bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                                      {sub.sub_product_image ? (
                                        <img
                                          src={getFullImageUrl(sub.sub_product_image)}
                                          alt={sub.sub_product_name}
                                          className="w-full h-full object-cover"
                                          onError={(e) => {
                                            (e.target as HTMLImageElement).parentElement?.classList.add('flex', 'items-center', 'justify-center');
                                            (e.target as HTMLImageElement).style.display = 'none';
                                            console.error('Cart SubProduct image failed:', sub.sub_product_name, sub.sub_product_image);
                                          }}
                                        />
                                      ) : null}
                                    </div>
                                  <div className="p-2 text-center flex flex-col justify-between h-14">
                                    <p className={`text-[9px] font-bold leading-tight line-clamp-2 ${isSelected ? 'text-primary' : 'text-gray-700 dark:text-gray-300'}`}>
                                      {sub.sub_product_name}
                                    </p>
                                    <p className={`text-[8px] font-black ${isSelected ? 'text-primary' : 'text-gray-500'}`}>
                                      +{currencySymbol}{sub.additional_price.toFixed(0)}
                                    </p>
                                  </div>
                                </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {/* Cart Offers */}
            {offers.map((item) => (
              <div
                key={item.offer.uuid}
                className="bg-gradient-to-r from-sky-50 to-sky-50 dark:from-sky-900/20 dark:to-sky-900/20 rounded-2xl p-5 shadow-lg border-2 border-sky-200 dark:border-sky-700 hover:shadow-xl transition-all duration-200"
              >
                <div className="flex items-start gap-4">
                  {item.offer.image_url || item.offer.image ? (
                    <img
                      src={getFullImageUrl(item.offer.image_url || item.offer.image || '')}
                      alt={item.offer.name}
                      className="w-20 h-20 rounded-xl object-cover shadow-md flex-shrink-0"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-sky-200 to-sky-200 dark:from-sky-800/30 dark:to-sky-800/30 flex items-center justify-center shadow-md">
                      <Sparkles className="w-8 h-8 text-sky-500" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-lg text-sky-900 dark:text-sky-100 mb-1 truncate">
                      {item.offer.name}
                    </h3>
                    <p className="text-sm text-sky-700 dark:text-sky-300 mb-3 truncate">
                      {currencySymbol}{item.offer.price.toFixed(2)} c/u • Oferta especial
                    </p>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <QuantityControl
                        quantity={item.quantity}
                        onIncrease={() => updateOfferQuantity(item.offer.uuid, item.quantity + 1)}
                        onDecrease={() => updateOfferQuantity(item.offer.uuid, item.quantity - 1)}
                      />
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => removeOffer(item.offer.uuid)}
                          className="p-2 text-primary hover:text-sky-700 hover:bg-sky-50 dark:hover:bg-sky-900/20 rounded-full transition-all duration-200"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                    {/* Observation Input (Offer) */}
                    <div className="mt-3">
                      <div className="flex items-center gap-2 mb-2 text-xs font-semibold text-sky-700/60 dark:text-sky-300/60">
                        <MessageSquare className="w-3 h-3" />
                        <span>Instrucciones para la oferta</span>
                      </div>
                      <textarea
                        value={item.observation || ''}
                        onChange={(e) => updateOfferObservation(item.offer.uuid, e.target.value)}
                        placeholder="Ej: Para compartir, sin aderezos..."
                        className="w-full p-3 text-sm border border-sky-100 dark:border-sky-800 rounded-xl bg-white/50 dark:bg-gray-900/50 text-sky-900 dark:text-sky-100 placeholder-sky-400 focus:ring-2 focus:ring-primary focus:border-transparent resize-none transition-all duration-200 shadow-sm"
                        rows={2}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Scheduled Orders Section */}
        {hasScheduledOrders() && (
          <div className="space-y-4">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Calendar className="w-5 h-5 text-indigo-500" />
              Pedidos Programados ({scheduledOrders.length})
            </h2>

            {scheduledOrders.map((order) => {
              const isExpanded = expandedDates.has(order.scheduledDate);
              const dateTotal = getTotalByDate(order.scheduledDate);
              const itemCount = order.items.reduce((sum, item) => sum + item.quantity, 0) +
                order.offers.reduce((sum, item) => sum + item.quantity, 0);

              return (
                <div
                  key={order.scheduledDate}
                  className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-2xl shadow-lg border-2 border-indigo-200 dark:border-indigo-700 overflow-hidden"
                >
                  {/* Date Header */}
                  <div
                    onClick={() => toggleDate(order.scheduledDate)}
                    className="p-4 cursor-pointer hover:bg-indigo-100/50 dark:hover:bg-indigo-800/30 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Calendar className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                        <div>
                          <h3 className="font-bold text-indigo-900 dark:text-indigo-100">
                            {formatDate(order.scheduledDate)}
                          </h3>
                          <p className="text-sm text-indigo-700 dark:text-indigo-300">
                            {itemCount} producto{itemCount !== 1 ? 's' : ''}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-bold text-lg text-indigo-900 dark:text-indigo-100">
                          {currencySymbol}{dateTotal.toFixed(2)}
                        </span>
                        <ChevronDown
                          className={`w-5 h-5 text-indigo-600 dark:text-indigo-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''
                            }`}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Expanded Items */}
                  {isExpanded && (
                    <div className="px-4 pb-4 space-y-3 border-t border-indigo-200 dark:border-indigo-700 pt-4">
                      {/* Scheduled Items */}
                      {order.items.map((item) => (
                        <React.Fragment key={item.cartItemId}>
                          <div
                            className="bg-white/70 dark:bg-gray-800/70 rounded-xl p-3 flex items-center gap-3"
                          >
                            <img
                              src={item.product.image}
                              alt={item.product.name}
                              className="w-12 h-12 rounded-lg object-cover"
                            />
                            <div className="flex-1">
                              <h4 className="font-semibold text-sm text-gray-900 dark:text-white">
                                {item.product.name}
                              </h4>
                              <p className="text-xs text-gray-600 dark:text-gray-400">
                                {currencySymbol}{item.product.price.toFixed(2)} c/u
                              </p>
                              {item.sub_products && item.sub_products.length > 0 && (
                                <div className="mt-1 flex flex-wrap gap-1">
                                  {item.sub_products.map(sub => (
                                    <span key={sub.sub_product_uuid} className="text-[9px] font-bold text-primary bg-sky-50 dark:bg-sky-900/30 px-1.5 py-0.5 rounded">
                                      +{sub.sub_product_name}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                            <QuantityControl
                              quantity={item.quantity}
                              onIncrease={() => updateScheduledQuantity(item.cartItemId || item.product.uuid, order.scheduledDate, item.quantity + 1)}
                              onDecrease={() => updateScheduledQuantity(item.cartItemId || item.product.uuid, order.scheduledDate, item.quantity - 1)}
                              size="sm"
                            />
                            <button
                              onClick={() => removeScheduledItem(item.cartItemId || item.product.uuid, order.scheduledDate)}
                              className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                          {/* Scheduled Item Observation / Sub-products Edit (Dynamic) */}
                          {item.product.sub_products && item.product.sub_products.length > 0 && (
                            <div className="mt-2 ml-15 flex gap-2 overflow-x-auto no-scrollbar pb-1">
                                {item.product.sub_products.map((sub) => {
                                    const isSelected = item.sub_products?.some(s => s.sub_product_uuid === sub.sub_product_uuid);
                                    return (
                                        <button
                                            key={sub.sub_product_uuid}
                                            onClick={() => {
                                                if (isSelected) {
                                                    updateScheduledItemSubProducts(item.cartItemId || item.product.uuid, order.scheduledDate, []);
                                                } else {
                                                    updateScheduledItemSubProducts(item.cartItemId || item.product.uuid, order.scheduledDate, [sub]);
                                                }
                                            }}
                                            className={`px-3 py-1 rounded-lg text-[10px] font-bold whitespace-nowrap transition-all border ${isSelected
                                                    ? 'bg-indigo-500 border-indigo-500 text-white'
                                                    : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-500'
                                                }`}
                                        >
                                            {sub.sub_product_name}
                                        </button>
                                    );
                                })}
                            </div>
                          )}
                        </React.Fragment>
                      ))}

                      {/* Scheduled Offers */}
                      {order.offers.map((item) => (
                        <React.Fragment key={item.offer.uuid}>
                          <div
                            className="bg-sky-50/70 dark:bg-sky-900/30 rounded-xl p-3 flex items-center gap-3 border border-sky-200 dark:border-sky-700"
                          >
                            {item.offer.image_url || item.offer.image ? (
                              <img
                                src={getFullImageUrl(item.offer.image_url || item.offer.image || '')}
                                alt={item.offer.name}
                                className="w-12 h-12 rounded-lg object-cover"
                              />
                            ) : (
                              <div className="w-12 h-12 rounded-lg bg-sky-200 dark:bg-sky-800 flex items-center justify-center">
                                <Sparkles className="w-6 h-6 text-sky-500" />
                              </div>
                            )}
                            <div className="flex-1">
                              <h4 className="font-semibold text-sm text-sky-900 dark:text-sky-100">
                                {item.offer.name}
                              </h4>
                              <p className="text-xs text-sky-700 dark:text-sky-300">
                                {currencySymbol}{item.offer.price.toFixed(2)} c/u
                              </p>
                            </div>
                            <QuantityControl
                              quantity={item.quantity}
                              onIncrease={() => updateScheduledOfferQuantity(item.offer.uuid, order.scheduledDate, item.quantity + 1)}
                              onDecrease={() => updateScheduledOfferQuantity(item.offer.uuid, order.scheduledDate, item.quantity - 1)}
                              size="sm"
                            />
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => removeScheduledOffer(item.offer.uuid, order.scheduledDate)}
                                className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          {/* Scheduled Offer Observation */}
                          <div className="mt-2 ml-15">
                            <textarea
                              value={item.observation || ''}
                              onChange={(e) => updateScheduledOfferObservation(item.offer.uuid, order.scheduledDate, e.target.value)}
                              placeholder="Nota..."
                              className="w-full p-2 text-xs border border-sky-100 dark:border-sky-800 rounded-lg bg-sky-50/50 dark:bg-gray-900/50 text-sky-900 dark:text-sky-100 placeholder-sky-400 focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none transition-all duration-200"
                              rows={1}
                            />
                          </div>
                        </React.Fragment>
                      ))}

                      {/* Remove entire date button */}
                      <button
                        onClick={() => removeScheduledDate(order.scheduledDate)}
                        className="w-full mt-2 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg font-medium transition-colors"
                      >
                        Eliminar todos los items de este día
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Notes Section */}
        <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-3 mb-4">
            <MessageSquare className="w-6 h-6 text-primary" />
            <h3 className="font-bold text-lg text-gray-900 dark:text-white">
              Notas para la cocina
            </h3>
          </div>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Instrucciones especiales, alergias, preferencias..."
            className="w-full p-4 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-sky-500 focus:border-transparent resize-none transition-all duration-200"
            rows={3}
          />
        </div>

        {/* Order Summary */}
        <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-2xl p-6 shadow-lg border border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-3 mb-6">
            <Receipt className="w-6 h-6 text-green-500" />
            <h3 className="font-bold text-lg text-gray-900 dark:text-white">
              Resumen del Pedido
            </h3>
          </div>

          <div className="space-y-4">
            {hasImmediateOrders() && (
              <div className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-700">
                <span className="text-gray-600 dark:text-gray-400 font-medium">Pedido Inmediato</span>
                <span className="text-gray-900 dark:text-white font-semibold">{currencySymbol}{total.toFixed(2)}</span>
              </div>
            )}

            {scheduledOrders.map((order) => (
              <div key={order.scheduledDate} className="flex justify-between items-center py-2 border-b border-gray-100 dark:border-gray-700">
                <span className="text-gray-600 dark:text-gray-400 font-medium text-sm">
                  {(() => {
                    const [y, m, d] = order.scheduledDate.split('-');
                    const date = new Date(parseInt(y), parseInt(m) - 1, parseInt(d));
                    return date.toLocaleDateString('es-AR', { month: 'short', day: 'numeric' });
                  })()}
                </span>
                <span className="text-gray-900 dark:text-white font-semibold">{currencySymbol}{getTotalByDate(order.scheduledDate).toFixed(2)}</span>
              </div>
            ))}

            <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl p-4">
              <div className="flex justify-between items-center">
                <span className="font-bold text-xl text-gray-900 dark:text-white">Total</span>
                <span className="font-bold text-2xl text-green-600 dark:text-green-400">
                  {currencySymbol}{grandTotal.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Fixed Bottom Button */}
      <div className="fixed bottom-20 left-1/2 -translate-x-1/2 w-full max-w-md px-4 z-40">
        <button
          onClick={handleProceedToOrder}
          className="w-full bg-gradient-to-r btn-primary text-white py-5 rounded-2xl font-bold text-lg transition-all duration-200 shadow-2xl hover:shadow-3xl hover:scale-105 flex items-center justify-center gap-3"
        >
          <Receipt className="w-6 h-6" />
          {((orderMode === 'delivery' || orderMode === 'pickup') && hasImmediateOrders())
            ? (orderMode === 'delivery' ? `Datos de envío • ${currencySymbol}${grandTotal.toFixed(2)}` : `Datos de retiro • ${currencySymbol}${grandTotal.toFixed(2)}`)
            : `Confirmar Pedido • ${currencySymbol}${grandTotal.toFixed(2)}`
          }
        </button>
      </div>

      {/* Bottom Navigation */}
      <BottomNavigation tableId={tableId || ''} />
    </div>
  );
}